import { X, Mic, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import Select from "../../../../../shared/ui/form/Select";
import Switch from "../../../../../shared/ui/form/switch/Switch";

interface LiveSessionSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    devices: { label: string; value: string }[];
    selectedDevice: string;
    onSelectDevice: (deviceId: string) => void;
    reminderEnabled: boolean;
    onToggleReminder: (enabled: boolean) => void;
    reminderMinutes: number;
    onChangeReminderMinutes: (minutes: number) => void;
    showSessionLimit?: boolean;
    disabled?: boolean;
}

export default function LiveSessionSettingsModal({
    isOpen,
    onClose,
    devices,
    selectedDevice,
    onSelectDevice,
    reminderEnabled,
    onToggleReminder,
    reminderMinutes,
    onChangeReminderMinutes,
    showSessionLimit = true,
    disabled = false
}: LiveSessionSettingsModalProps) {
    const { t } = useTranslation();

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-9999999 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200">

                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b bg-gray-50">
                    <h3 className="text-lg font-bold text-gray-800">{t("session.live.settingsTitle", "Recording Settings")}</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200 transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                    {/* Microphone Selector */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <Mic className="w-4 h-4" />
                            {t("session.live.inputDevice", "Microphone")}
                        </label>
                        <Select
                            options={devices}
                            value={selectedDevice}
                            onChange={onSelectDevice}
                            placeholder={t("session.live.selectMic", "Select microphone")}
                            disabled={disabled}
                        />
                    </div>

                    {/* Session Timer Reminder */}
                    {showSessionLimit && (
                        <div className="space-y-2">
                            <div className="flex justify-between items-center h-5">
                                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                    <Clock className="w-4 h-4" />
                                    {t("session.live.sessionLimit", "Session Limit")}
                                </label>
                                <Switch
                                    label=""
                                    defaultChecked={reminderEnabled}
                                    onChange={onToggleReminder}
                                    color="blue"
                                    disabled={disabled}
                                />
                            </div>

                            <div className={`transition-opacity duration-200 ${reminderEnabled ? "opacity-100" : "opacity-30 pointer-events-none"}`}>
                                <div className="relative">
                                    <input
                                        type="number"
                                        min="1"
                                        value={reminderMinutes}
                                        onChange={(e) => onChangeReminderMinutes(Number(e.target.value))}
                                        disabled={disabled || !reminderEnabled}
                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
                                    />
                                    <span className="absolute right-3 top-2.5 text-content-tertiary text-sm">min</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-gray-50 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-accent hover:bg-accent-hover text-white rounded-lg text-sm font-medium transition-colors"
                    >
                        {t("common.close", "Close")}
                    </button>
                </div>
            </div>
        </div>
    );
}
